import { useState } from 'react'
import { Percent, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile } from '@/api/resourceCenterApi'
import { splitSegments, WORK_TYPE_CHIPS } from '@/lib/assignmentGroups'
import { Empty, ErrorLine, SectionCard, WORK_COLOR, longDate, useProfileWrite } from './shared'
import { SplitDialog } from './SplitDialog'

// Re-exported for existing importers (ResourcesList.tsx) — the colors and
// absence stripes now live in shared.tsx, the one place every profile file
// (and the People grid) reads them from.
export { ABSENCE_STRIPES, WORK_COLOR } from './shared'

/**
 * Assignment tab, split half (slides 2 and 4): the current % Assigned (or
 * Dynamic) at a glance, its history, and a "+ New split" button that opens
 * the editor in a dialog.
 */
export function SplitSection({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const current = profile.split
  const write = useProfileWrite()
  const segments = splitSegments(current)
  const history = [...profile.splits].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))

  return (
    <SectionCard
      icon={Percent}
      title='% Assigned'
      hint='How net hours split between the work'
      action={
        canManage && (
          <Button size='sm' className='h-8' onClick={() => setDialogOpen(true)}>
            <Plus className='mr-1 h-3.5 w-3.5' /> New split
          </Button>
        )
      }
    >
      {!current ? (
        <Empty>No split set yet — work cannot be assigned until one is.</Empty>
      ) : current.isDynamic ? (
        <div className='flex flex-wrap gap-1.5'>
          {current.workTypes.map(t => (
            <span key={t} className={cn('rounded-full px-3 py-1 text-xs font-semibold', WORK_TYPE_CHIPS[t].className)}>
              {WORK_TYPE_CHIPS[t].label} · {WORK_TYPE_CHIPS[t].title}
            </span>
          ))}
          <span className='self-center text-xs text-muted-foreground'>Dynamic — split as needed by the scheduler</span>
        </div>
      ) : (
        <div className='space-y-2'>
          <div className='flex h-2 overflow-hidden rounded-full bg-muted'>
            {segments.map(s => (
              <span key={s.type} className={WORK_COLOR[s.type]} style={{ width: `${s.pct}%` }} />
            ))}
          </div>
          <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs'>
            {segments.length === 0 ? (
              <span className='text-muted-foreground'>0% — nothing assigned</span>
            ) : (
              segments.map(s => (
                <span key={s.type} className='font-medium'>
                  {WORK_TYPE_CHIPS[s.type].label} <span className='text-muted-foreground'>{s.pct}%</span>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      <ErrorLine message={write.error} />

      {history.length > 0 && (
        <ul className='divide-y rounded-lg border text-xs'>
          {history.map(s => (
            <li key={s.id} className='flex items-center justify-between gap-2 px-2.5 py-1.5'>
              <span className='text-muted-foreground'>From {longDate(s.effectiveFrom)}</span>
              <span className='font-mono'>
                {s.isDynamic
                  ? `Dynamic · ${s.workTypes.map(t => WORK_TYPE_CHIPS[t].label).join(' ')}`
                  : [s.processPct, s.reviewPct, s.qaPct, s.supportPct].join(' · ')}
              </span>
              {canManage && (
                <button
                  type='button'
                  aria-label={`Remove the split from ${s.effectiveFrom}`}
                  className='text-muted-foreground/60 hover:text-destructive'
                  onClick={() => write.run(() => resourceProfilesApi.removeSplit(profile.userId, s.id))}
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {/* Mounted only while open, so each opening starts from a clean form. */}
      {canManage && dialogOpen && <SplitDialog profile={profile} open onOpenChange={setDialogOpen} />}
    </SectionCard>
  )
}
